import { ConfirmationResultObj, WizBaseView, WizCommandManager, WizIcon, WizPopUp, WizRoot, WizEventManager, ToastManager } from '@wizeoo/akm-component-lib';
import { Model } from '../../model/model';
import { APP_ORIGIN, CONFIRMATION_MODE, CONFIRMATION_RESULT, WIZ_COMMAND_STATE } from '@wizeoo/akm-core-lib';
import { WizTranslationManager } from '@wizeoo/akm-component-lib';
import { ALK_EVENT_TYPE, CONNECT_RETURN_ACTION } from '../../model/enums';
import { AlkEvent } from '../../framework/events/alk.event';
import { ConnectVO } from '../../vo/core/connect.vo';
import { ConnectReturnRenderer } from '../core/connectrenderers/connect-return-renderer';
import { EnvironnementManager } from '../../model/environnement.manager';
import { Baseline, DataPack, DataPackSet, SocketMessage } from '@wizeoo/akm-message-lib';
import { DataCommitCmd } from '../../command/em/datacommit.cmd';
import { DataLoadObj, DataLooper, EM_ACTION, LoopResult, LoopVO, WidgetCell, WizConstants } from '@wizeoo/akm-compute-lib';


// origine du message
export enum SOCKET_MESSAGE_ORIGIN {
    COMPUTE = 'COMPUTE',
    BATCH = 'BATCH',
    CONNECT = 'CONNECT',
    AKM = 'AKM',
}

// sous-types normalisés
export enum SOCKET_MESSAGE_KIND {
    BATCH_EXPORT = 'BATCH_EXPORT', //retour sur export
    BATCH_IMPORT = 'BATCH_IMPORT', // retour sur import
    COMMIT = 'COMMIT', // retour de commit
    WORKCOM = 'WORKCOM', // retour de commit sur WORKCOM
    CROSS_VISION = 'CROSS_VISION', // modif me concernant, alimentée en cross-vision
    COMPUTE_ENDED = 'ended', // fin de calcul compute
    REFRESH_LOG = 'REFRESH_LOG', // Rafraîchissement de la vision de restitution des logs Connect
    REFRESH_VISION = 'REFRESH'  // Rafraîchissement d'une vision
}

// type de succès / échec
export enum SOCKET_MESSAGE_RESULT {
    BATCH_SUCCESS = 'ENDED',
    BATCH_FAILURE = 'FAILED',
    CONNECT_SUCCESS = 'SUCCESS',
    CONNECT_FAILURE = 'ERROR',
}


/**
 * Moteur de traitement d'un message websocket
 * 
 * Dans le front ME, le message peut être issu de la fin d'un traitement batch d'export de données ou d'import de fichier, 
 * d'un flux connect (manuel et synchrone) ou un traitement compute
 */
export class SocketMessageEngine {

    protected _model: Model = null;
    constructor(model: Model) {
        this._model = model;
    }

    public get model(): Model {
        return this._model;
    }

    /**
     * correspondance entre l'origine du message et mon origine (model.origin)
     * pour éviter les messages DOMs dans AKM
     * @param message 
     * @returns 
     */
    private checkOrigin(message: SocketMessage): boolean {
        let result: boolean = true;


        let myOrigin: string = this._model.origin;
        let myUUID: string = WizRoot.getInstance().uuid;
        // console.log('checkOrigin', message.origin, myOrigin, myUUID);

        switch (message.origin) {
            case SOCKET_MESSAGE_ORIGIN.BATCH:
            case SOCKET_MESSAGE_ORIGIN.CONNECT:
                switch (message.kind) {
                    case SOCKET_MESSAGE_KIND.REFRESH_LOG:
                        // Rafraîchissement de la vision de restitution des logs Connect
                        // on est sur du cross-vision... donc on accepte simplement si on est sur AKM
                        result = myOrigin === APP_ORIGIN.AKM;
                        break;
                    case SOCKET_MESSAGE_KIND.REFRESH_VISION:
                        // Rafraîchissement de la vision
                        // on est sur du cross-vision... donc on accepte simplement si on est sur AKM
                        result = myOrigin === APP_ORIGIN.AKM;
                        break;
                    default:
                        // Traitement du résultat d'une fonction Connect
                        result = myUUID === message.originUuid;
                        break;
                }
                break;
            case SOCKET_MESSAGE_ORIGIN.COMPUTE:
                result = myOrigin === APP_ORIGIN.AKM;
                break;
            case SOCKET_MESSAGE_ORIGIN.AKM:
                result = myOrigin === APP_ORIGIN.AKM;
                break;
        }
        return result;
    }

    private checkLogin(message: SocketMessage): boolean {
        let result: boolean = true;
        if (message.login !== this._model.connectedUser.mail) {
            result = false;
        }
        return result;
    }

    private checkCurrentDomain(message: SocketMessage): boolean {
        let result: boolean = true;
        let currentDomain = this.model.origin === APP_ORIGIN.DOM ? this.model.currentDomainCode : this.model.domainCode;
        if (message.domain !== currentDomain) {
            result = false;
        }
        return result;
    }

    private checkCurrentBu(message: SocketMessage): boolean {
        let result: boolean = true;
        // Si on vient de Connect et que l'on est sur du refresh cross-vision (log ou vision), on n'est pas nécessairement sur la même bu (ex: une bu de supervision))
        // Dans le cas, contraire, on doit être sur la même bu
        if (message.origin !== SOCKET_MESSAGE_ORIGIN.CONNECT || (message.kind !== SOCKET_MESSAGE_KIND.REFRESH_LOG && message.kind !== SOCKET_MESSAGE_KIND.REFRESH_VISION)) {
            let currentBu = this.model.origin === APP_ORIGIN.DOM ? this.model.currentBUCode : this.model.businessUnitCode;
            if (message.bu !== currentBu && !((message.bu === 'WIZEOO' && currentBu === '*'))) {
                result = false;
            }
        }
        return result;
    }

    private checkVision(message: SocketMessage): boolean {
        let result: boolean = true;
        let currentVision = this.model.currentVision?.code;
        
        if(!!(message.infos as any)?.inFreedata) { // dispatch des events sur les FDVs...
            result = true;
        }
        else if (message.vision !== currentVision) {
            result = false;
        }
        return result;
    }

    /**
     * Contrôle du périmètre du message
     * Un message doit être fourni et l'utilisateur courant doit être à l'origine du message,
     * et si l'utilisateur n'a pas changé de domaine ou de bu en cours de traitement
     * 
     * @param message Le message websocket à traiter
     * @returns True si le message est fourni et le périmètre valide, false dans le cas contraire
     */
    private validPerimeter(message: SocketMessage): boolean {

        if (!message) {
            return false;
        }

        let result: boolean = false;

        switch (message.origin) {
            case SOCKET_MESSAGE_ORIGIN.BATCH:
                // pas de contrôle d'origine sur les batchs, on mange de force
                result = this.checkOrigin(message) && this.checkLogin(message) && this.checkCurrentDomain(message) && this.checkCurrentBu(message);
                break;
            case SOCKET_MESSAGE_ORIGIN.CONNECT:
                result = this.checkOrigin(message) && this.checkLogin(message) && this.checkCurrentDomain(message) && this.checkCurrentBu(message);
                break;
            case SOCKET_MESSAGE_ORIGIN.COMPUTE:
                result = this.checkOrigin(message) && this.checkVision(message) && this.checkCurrentDomain(message) && this.checkCurrentBu(message);
                break;
            case SOCKET_MESSAGE_ORIGIN.AKM:
                result = this.checkOrigin(message) && this.checkVision(message) && this.checkCurrentDomain(message) && this.checkCurrentBu(message);
                break;
        }
        return result;
    }

    /**
     * Traitement du message websocket
     * 
     * @param pElemRef L'élément du DOM à éventuellement rafraichir
     * @param elementRef L'élément du DOM à éventuellement rafraichir
     * @param message Le message websocket à traiter (un string)
     */
    public do(view: WizBaseView, message: SocketMessage) {

        // console.log('view', view.elementRef, message);
        this.validCrossPerimeter(message); // alimentation du crossVision

        if (!this.validPerimeter(message)) { // pas valable, fini
            return;
        }


        switch (message.origin) {
            case SOCKET_MESSAGE_ORIGIN.BATCH:
                switch (message.kind) {
                    case SOCKET_MESSAGE_KIND.BATCH_EXPORT:
                        this.doBatchExport(view, message);
                        break;
                    case SOCKET_MESSAGE_KIND.BATCH_IMPORT:
                        this.doBatchImport(view, message);
                        break;
                }
                break;
            case SOCKET_MESSAGE_ORIGIN.CONNECT:
                switch (message.kind) {
                    case SOCKET_MESSAGE_KIND.REFRESH_LOG:
                        // Rafraîchissement de la vision de restitution des logs Connect
                        this._model.compositeEngine.compositeCheck();
                        break;
                    case SOCKET_MESSAGE_KIND.REFRESH_VISION:
                        // Rafraîchissement de la vision 
                        this._model.compositeEngine.compositeCheck();
                        break;
                    default:
                        // Traitement du résultat d'une fonction Connect
                        this.doConnect(view, message);
                        break;
                }
                break;
            case SOCKET_MESSAGE_ORIGIN.COMPUTE:
                this.doRefreshRemote(view, message);
                // console.log('COMPUTE', message);
                break;
            case SOCKET_MESSAGE_ORIGIN.AKM:
                switch (message.kind) {
                    case SOCKET_MESSAGE_KIND.COMMIT:
                        this.doCommit(view, message);
                        // console.log('COMMIT', message);
                        break;
                    case SOCKET_MESSAGE_KIND.WORKCOM:
                        this.doWorkcom(view, message);
                        // console.log('COMMIT', message);
                        break;
                    case SOCKET_MESSAGE_KIND.CROSS_VISION:
                        this.doCrossVision(view, message);
                        // console.log('COMMIT', message);
                        break;
                }

                break;
        }
    }

    /**
     * event AKM : COMMIT (par un autre, si c'est moi, je suis déjà à jour)
     * @param view 
     * @param socketMessage 
     */
    private doCommit(view: WizBaseView, socketMessage: SocketMessage): void {

        let self: boolean = false;
        if (socketMessage.originUuid === WizRoot.getInstance().uuid) {
            self = true;
        }
        if (!self) {
            // console.log(' #### doCommit other player', socketMessage);
            this._model.compositeEngine.compositeCheck();
        }
    }

    /**
     * event AKM : WORKCOM (par un autre, si c'est moi, je suis déjà à jour)
     * @param view 
     * @param socketMessage 
     */
    private doWorkcom(view: WizBaseView, socketMessage: SocketMessage): void {
        // console.log("doWorkcom", socketMessage);
        let self: boolean = false;
        if (socketMessage.originUuid === WizRoot.getInstance().uuid) {
            self = true;
        }
        if (!self) {
            // console.log(' #### doCommit other player', socketMessage);
            this._model.workflowEngine.check();
        }
    }

    /**
     * event COMPUTE : fin de calcul, forcer le refresh
     * @param view 
     * @param socketMessage 
     */
    private doRefreshRemote(view: WizBaseView, socketMessage: SocketMessage): void {

        // ne concerne que les grids remote. Pour les autres, j'ai déjà reçu un retour de type 'COMMIT'

        if (socketMessage.kind === SOCKET_MESSAGE_KIND.COMPUTE_ENDED) {
            // console.log('socket', socketMessage);
            WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.COMPUTE_UPDATE, socketMessage));
        }
    }

    private showPop(message: string, icon: any): void {
        WizPopUp.toast(message, icon,
            ToastManager.defaultTimeOut,
            ToastManager.defaultShowCloseButton,
            ToastManager.defaultTapClose,
            '350px', '120px').show();

        // WizPopUp.toast(message, icon).show();
    }

    /** 
     * Traitement de la confirmation de téléchargement du fichier généré
    */
    private confirmDownload(result: ConfirmationResultObj): void {
        if (result.result === CONFIRMATION_RESULT.CONFIRM) {
            const socketMessage: SocketMessage = result.data;
            const a = document.createElement("a");
            document.body.appendChild(a);
            // TODO TODO url
            let url: string = EnvironnementManager.envConfig.batchUrl;

            let infos: any = socketMessage.infos;
            let messageUrl: string = infos.fileUrl;
            let messageFileName: string = infos.fileName;

            WizCommandManager.httpClient.get(url + messageUrl, { responseType: 'blob' }).subscribe(blob => {
                const fileUrl = window.URL.createObjectURL(blob);
                a.href = fileUrl;
                a.download = messageFileName;
                a.click();
                document.body.removeChild(a);
            });
        }
    }

    private doBatchExport(view: WizBaseView, socketMessage: SocketMessage): void {

        let infos: any = socketMessage.infos;
        let firstError: string = infos?.firstError || null;
        let messageFileName: string = infos?.fileName || null;

        if (socketMessage.status !== SOCKET_MESSAGE_RESULT.BATCH_SUCCESS) {
            // Message d'erreur générique par défaut
            let errorMessage: string = '$.batch.failed.export.$';

            // On a récupéré un message d'erreur, on l'affiche
            if (!!firstError) {
                errorMessage = firstError;
            }
            this.showPop(errorMessage, WizIcon.TOAST_ERROR);
            return;
        }

        //### ça à fonctionné
        if (messageFileName === null) {
            // Export qui n'a pas généré de fichier, juste une pop pour prévenir l'utilisateur
            this.showPop('$.batch.completed.export.without.data.$', WizIcon.TOAST_WARNING);
        }
        else {
            // Export qui a généré un fichier, confirmation du téléchargement du fichier
            WizPopUp.confirm(
                '$.batch.completed.export.$',
                WizTranslationManager.getInstance().translateKey('$.batch.confirm.download.$', [messageFileName]),
                this.confirmDownload.bind(this),
                CONFIRMATION_MODE.CONFIRM_CANCEL,
                socketMessage, null, null).show();
        }
    }

    private doBatchImport(view: WizBaseView, socketMessage: SocketMessage): void {

        let infos: any = socketMessage.infos;
        let messageFileName: string = infos?.fileName || null;
        let entity: string = infos?.entity || null;

        if (socketMessage.status !== SOCKET_MESSAGE_RESULT.BATCH_SUCCESS) {
            // Message d'erreur générique par défaut
            let errorMessage: string = WizTranslationManager.getInstance().translateKey('$.batch.failed.import.detail.$', [messageFileName]);
            this.showPop(errorMessage, WizIcon.TOAST_ERROR);
            return;
        }

        //### ça à fonctionné
        let returnMessage: string = WizTranslationManager.getInstance().translateKey('$.batch.completed.import.detail.$', [messageFileName]);
        this.showPop(returnMessage, WizIcon.TOAST_SUCCESS);
        WizEventManager.dispatchEvent(view.elementRef, new AlkEvent(ALK_EVENT_TYPE.BATCH_IMPORT_RETURN, entity));
    }

    private doConnect(view: WizBaseView, socketMessage: SocketMessage): void {

        // TODO TODO traiter les erreurs connect (flux C2)

        let infos: any = socketMessage.infos;
        let baseError: string = WizTranslationManager.getInstance().translateKey('$.vision.toast.edit.error.$');
        let error: string = null;
        try {
            error = [infos?.error?.code, infos?.error?.message].join('</br>');
        } catch (e: any) {

        }
        let connectFunctionCode: string = infos?.connectFunctionCode || null;

        if (socketMessage.status !== SOCKET_MESSAGE_RESULT.CONNECT_SUCCESS) {

            let errMess: string = [baseError, connectFunctionCode, error].join('</br>');

            // Message d'erreur générique par défaut
            this.showPop(errMess, WizIcon.TOAST_ERROR);
            return;
        }

        //### ça à fonctionné
        let cnx: ConnectVO = this.model.connectEngine.getConnect(connectFunctionCode);

        if (cnx.resultAction === CONNECT_RETURN_ACTION.POP) {
            let dataMessagePop: WizPopUp;
            const targetPosition: any = { right: '0px', bottom: '50px' }; // position physique
            dataMessagePop = new WizPopUp(
                ConnectReturnRenderer,
                true,
                false,
                null,
                '700px', // 350px',
                '350px', // calc(100% - 81px)',
                null, // params
                null,
                targetPosition
            );

            dataMessagePop.datas = [cnx, infos];
            dataMessagePop.show();

        } else if (cnx.resultAction === CONNECT_RETURN_ACTION.REFRESH) {
            WizPopUp.toast(cnx.trsResultSuccess || '$.call.success.$', WizIcon.TOAST_SUCCESS).show();
            setTimeout(() => {
                // dispatch pour forcer le reload
                WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.COMPOSITE_RELOAD, cnx)); // on le signale

            }, (5000));
        }
    }

    /**
     * event AKM : MAJ me concernant issu d'un cross-vision
     * @param view 
     * @param socketMessage 
     * @deprecated
     */
    private doCrossVision(view: WizBaseView, socketMessage: SocketMessage): void {

        let dataPack: DataPack = null;

        try {
            // prise du numRev Max pour ce pack
            dataPack = socketMessage.infos as DataPack;
            console.log(' #### socket cross', socketMessage);
            this._model.compositeEngine.compositeCheck();

            // on va jouer un retour de commit
            // let cmd: DataCommitCmd = new DataCommitCmd(this._model);
            // cmd.result = dataPack
            // cmd.state = WIZ_COMMAND_STATE.SUCCESS;
            // console.log(' #### socket cross cmd', cmd);
            // this._model.compositeEngine.backFromDataCommit(cmd, true);

        } catch (e: any) {
            console.log(' #### socket cross oups', e, socketMessage);
        }
    }

    /**
     * Contrôle du périmètre du message pour du cross vision
     * Un message doit être fourni et l'utilisateur courant doit être à l'origine du message,
     * et si l'utilisateur n'a pas changé de domaine ou de bu en cours de traitement
     * 
     * @param message Le message websocket à traiter
     * @returns True si le message est fourni et le périmètre valide, false dans le cas contraire
     */
    private validCrossPerimeter(message: SocketMessage): void {

        if (!this.model.dataEngine.hasCrossed()) {
            return;
        }
        // on vérifie même domaine & bu, vision différente
        let currentVision = this.model.currentVision?.code;
        let crossedVision: boolean = this.checkCurrentDomain(message) && this.checkCurrentBu(message) && message.vision !== currentVision;
        if(!crossedVision) { // autre périmètre ou même vision
            return;
        }

        // console.log("crossed #vision", message);
        if(crossedVision) {  
            crossedVision = false;
            [...this.model.dataEngine.crossVisions.keys()].forEach(k => {
                let sk: string = k.split(WizConstants.CONTEXT_SEPARATOR)[0];
                if(sk === message.vision) { // j'ai une dépendance sur cette vision
                    crossedVision = true;
                }
            });
        }

        if(!crossedVision) {
            return;
        }

        // console.log("crossed ==vision", crossedVision);
        let datapack: DataPack = null;
        try {
            // prise du numRev Max pour ce pack
            datapack = message.infos as DataPack;
            // console.log(' #### socket cross', datapack);
            // injection en BDD local // TODO vérifier
            this.model.widgetDataService.eatRemote(datapack);
            
            // maj du dataEngine._crossedLinesMap
            // prise des cellules à rejouer
            let updatePool: Set<WidgetCell> = new Set();

            // on alimente les jeux locaux et on recalcul
            let loopvo: LoopVO = { loopResults: new Map(), pendings: new Map()};
            // TODO alimenter
            // let newPack: DataPack = new DataPack();
            let pendingMap: Map<number, Map<string, Set<WidgetCell>>> = new Map();
            loopvo.pendings = pendingMap;

            if (!!datapack.datas) {
                 Object.keys(datapack.datas).forEach((lvlStr: string) => {
                   Object.keys(datapack.datas[lvlStr]).forEach((scen: string) => {   
                        let datas: DataPackSet = datapack.datas[lvlStr][scen];
                        datas.baselines?.forEach(bl => { // y'a les deletes, les updates et les create
                            
                            let exist: Baseline = this.model.dataEngine.getCrossedLine(bl.v, bl.level, bl.scen, bl.key);
                            if(exist) { // on va jouer des updates
                                let updates: Set<WidgetCell> = this.model.cellMatrixEngine.crossDepsMap.get(exist.id);
                                if(updates?.size > 0) {
                                    [...updates.keys()].forEach(wc => {
                                        if(!pendingMap.has(wc.l)) {
                                            pendingMap.set(wc.l, new Map());
                                        }
                                        if(!pendingMap.get(wc.l).has(wc.sc)) {
                                            pendingMap.get(wc.l).set(wc.sc, new Set());
                                        }
                                        pendingMap.get(wc.l).get(wc.sc).add(wc);
                                    });
                                }
                            } 
                            if(bl.operation === EM_ACTION.DELETE) { // suppression, on rince les valeurs
                                this.model.dataEngine.deleteCrossedLine(bl.v, bl.level, bl.scen, bl.key);
                                this.model.cellMatrixEngine.crossDepsMap.delete(bl.id);
                            } else {
                               this.model.dataEngine.addOrUpdateCrossedLine(bl); 
                            }
                           
                        });
                        datas.baselineValues?.forEach(blv => { // créate ou update, peu importe, la ligne existe
                            let exist: Baseline = this.model.dataEngine.getCrossedLine(blv.v, blv.level, blv.scen, blv.key);
                            if(exist) { // on va jouer des updates
                                let updates: Set<WidgetCell> = this.model.cellMatrixEngine.crossDepsMap.get(exist.id);
                                if(updates?.size > 0) {
                                    [...updates.keys()].forEach(wc => {
                                        if(!pendingMap.has(wc.l)) {
                                            pendingMap.set(wc.l, new Map());
                                        }
                                        if(!pendingMap.get(wc.l).has(wc.sc)) {
                                            pendingMap.get(wc.l).set(wc.sc, new Set());
                                        }
                                        pendingMap.get(wc.l).get(wc.sc).add(wc);
                                    });
                                }
                                if(!exist.values) {
                                    exist.values = {};
                                }
                                exist.values[blv.mkey] = blv;
                            } 
                            // console.log('blv', blv);
                        });
                    });
                });
            }

            // maj des pendings pour replay
            [...updatePool.keys()].forEach(wc => {
                this.model.cellMatrixEngine.addPendingCell(wc.l, wc);
            });

            // détecter les FEI à rejouer
            // console.log('updatePool', updatePool, this.model.cellMatrixEngine.pendingCellMap);
            // let loopResult: LoopResult = DataLooper.getLooperResult(loopResults, fei); // le résultat dans lequel on va pousser les updates
            this.model.dataEngine.recomputePendings(loopvo);
            this.model.dataEngine.finalizeCompute(loopvo.loopResults, null);
            // TODO forcer le refresh sur les compos


        } catch (e: any) {
            console.log(' #### socket cross oups', e, message);
        }


    }


}